// ---------------------------------------------------------------------------
// DW.2: Agent Task List — Overview of all agent tasks
// ---------------------------------------------------------------------------
// Shows pending approvals prominently, active tasks, and history.
// Level 0: approval cards shown for every proposed action.
import { Bot, CheckCircle2, XCircle, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { AgentApprovalCard } from "./agent-approval-card";
import type { AgentTask, AgentState } from "../../hooks/use-agent-tasks";
import { Skeleton } from "../skeleton";

// ---------------------------------------------------------------------------
// State badge
// ---------------------------------------------------------------------------

const STATE_CONFIG: Record<AgentState, { label: string; color: string; icon: typeof Bot }> = {
  idle: { label: "Idle", color: "var(--color-muted-foreground)", icon: Clock },
  thinking: { label: "Thinking...", color: "var(--color-primary)", icon: Loader2 },
  action_proposed: { label: "Awaiting Approval", color: "var(--color-warning)", icon: AlertTriangle },
  approved: { label: "Approved for Execution", color: "var(--color-success)", icon: CheckCircle2 },
  executing: { label: "Executing...", color: "var(--color-primary)", icon: Loader2 },
  complete: { label: "Complete", color: "var(--color-success)", icon: CheckCircle2 },
  failed: { label: "Failed", color: "var(--color-destructive)", icon: XCircle },
  rejected: { label: "Rejected", color: "var(--color-muted-foreground)", icon: XCircle },
};

const AGENT_NAMES: Record<string, string> = {
  lead_qualifier: "Lead Qualifier",
  review_responder: "Review Responder",
  appointment_reminder: "Appointment Reminder",
  email_drafter: "Email Drafter",
  data_enrichment: "Data Enrichment",
};

function getExecutionStatusCopy(task: AgentTask): string {
  if (task.state === "approved") {
    return "Approved. Actions execute automatically via internal API calls.";
  }
  return task.executionMessage;
}

function StateBadge({ state }: { state: AgentState }) {
  const config = STATE_CONFIG[state];
  const Icon = config.icon;
  const isActiveState = state === "thinking" || state === "executing";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
      style={{ color: config.color, backgroundColor: `color-mix(in srgb, ${config.color} 10%, transparent)` }}
    >
      {isActiveState ? <Skeleton className="h-3 w-3" /> : <Icon size={12} />}
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AgentTaskListProps {
  tasks: AgentTask[];
  pendingApproval: AgentTask[];
  onApprove: (taskId: string, modifiedArgs?: Record<string, unknown>) => void;
  onReject: (taskId: string, reason: string) => void;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentTaskList({
  tasks,
  pendingApproval,
  onApprove,
  onReject,
  isLoading = false,
}: AgentTaskListProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-4 w-40" />
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                key={index}
                className="rounded-lg border px-4 py-3"
                style={{
                  borderColor: "var(--color-border)",
                  backgroundColor: "var(--color-card)",
                }}
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-5 w-24 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="rounded-lg border px-4 py-3"
              style={{
                borderColor: "var(--color-border)",
                backgroundColor: "var(--color-card)",
              }}
            >
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-5 w-28 rounded-full" />
                  </div>
                  <Skeleton className="h-3 w-56" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tasks.length === 0 && !isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-center"
        style={{ color: "var(--color-muted-foreground)" }}
      >
        <Bot size={48} className="mb-4 opacity-30" />
        <p className="text-sm font-medium">No agent tasks yet</p>
        <p className="text-xs mt-1">Create a task to get started with AI agents</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending approvals — prominent */}
      {pendingApproval.length > 0 && (
        <div>
          <h3
            className="text-sm font-medium mb-3 flex items-center gap-2"
            style={{ color: "var(--color-warning)" }}
          >
            <AlertTriangle size={14} />
            Pending Approval ({pendingApproval.length})
          </h3>
          <div className="space-y-3">
            {pendingApproval.map((task) =>
              task.proposedAction ? (
                <AgentApprovalCard
                  key={task.id}
                  taskId={task.id}
                  agentName={AGENT_NAMES[task.agentType] ?? task.agentType}
                  agentType={task.agentType}
                  proposedAction={task.proposedAction}
                  onApprove={onApprove}
                  onReject={onReject}
                />
              ) : null,
            )}
          </div>
        </div>
      )}

      {/* All tasks */}
      <div>
        <h3
          className="text-sm font-medium mb-3"
          style={{ color: "var(--color-foreground)" }}
        >
          All Tasks ({tasks.length})
        </h3>
        <div className="space-y-2">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="stagger-item flex items-center gap-3 px-4 py-3 rounded-lg border"
              style={{
                borderColor: "var(--color-border)",
                backgroundColor: "var(--color-card)",
              }}
            >
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                style={{ backgroundColor: "var(--color-muted)" }}
              >
                <Bot size={16} style={{ color: "var(--color-muted-foreground)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate" style={{ color: "var(--color-foreground)" }}>
                    {AGENT_NAMES[task.agentType] ?? task.agentType}
                  </span>
                  <StateBadge state={task.state} />
                </div>
                <p className="text-xs truncate mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                  {task.goal}
                </p>
                {task.state === "approved" ? (
                  <p className="text-xs mt-1" style={{ color: "var(--color-muted-foreground)" }}>
                    {getExecutionStatusCopy(task)}
                  </p>
                ) : null}
              </div>
              {task.result && (
                <div className="text-right flex-shrink-0">
                  <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                    {task.result.actionsExecuted} action{task.result.actionsExecuted !== 1 ? "s" : ""}
                  </p>
                  <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                    {task.result.totalDurationMs < 1000
                      ? `${task.result.totalDurationMs}ms`
                      : `${(task.result.totalDurationMs / 1000).toFixed(1)}s`}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
